import Heading from '@atlaskit/heading';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { Box, xcss } from '@atlaskit/primitives';
import React, { type FunctionComponent } from 'react';

import ButtonItem from './ButtonItem';
import { type MenuPage } from './hooks/useMenuNav';

const drilldownHeaderStyles = xcss({
  paddingBlockStart: 'space.075',
});

const drilldownTitleStyles = xcss({
  paddingBlockStart: 'space.200',
  paddingBlockEnd: 'space.100',
  paddingInline: 'space.200',
});

/**
 * A menu item content type that triggers in-place navigation (drilldown).
 *
 * When assigned to a `MenuItem.content`, clicking the item pushes this page
 * onto the menu stack, rendering a back-button header and the page's content.
 */
export type DrilldownMenu = MenuPage & {
  readonly type: 'drilldown-menu';
  title: string;
};

export const isDrilldownMenu = (value: unknown): value is DrilldownMenu =>
  typeof value === 'object' &&
  value !== null &&
  'type' in value &&
  (value as DrilldownMenu).type === 'drilldown-menu';

type DrilldownHeaderProps = {
  title: string;
  showBackButton: boolean;
  onBack: () => void;
  backButtonRef: React.Ref<HTMLElement>;
  backButtonLabel: string;
};

export const DrilldownHeader: FunctionComponent<DrilldownHeaderProps> = ({
  title,
  showBackButton,
  onBack,
  backButtonRef,
  backButtonLabel,
}) =>
  showBackButton ? (
    <Box xcss={drilldownHeaderStyles} testId="drilldown-header">
      <ButtonItem
        ref={backButtonRef}
        role="menuitem"
        onClick={onBack}
        iconBefore={<ChevronLeftIcon label={backButtonLabel} />}
        testId="drilldown-back-button"
      >
        <Heading size="xsmall" as="span">
          {title}
        </Heading>
      </ButtonItem>
    </Box>
  ) : (
    <Box xcss={drilldownTitleStyles} testId="drilldown-title">
      <Heading size="xsmall" as="span">
        {title}
      </Heading>
    </Box>
  );

/** Wrapper for key event capture during drilldown navigation. */
export const DrilldownWrapper: FunctionComponent<{
  active: boolean;
  onKeyDown: (e: React.KeyboardEvent) => void;
  children: React.ReactNode;
}> = ({ active, onKeyDown, children }) =>
  active ? (
    <Box role="presentation" onKeyDown={onKeyDown}>
      {children}
    </Box>
  ) : (
    <>{children}</>
  );
